import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { RequestService } from '@app/core/request/request.service';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, share, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import * as fromRoot from '@app/store/app.reducer';
import { Review } from '@app/core/interfaces';
import { muteFirst } from '@app/core/helpers/mute-first';
import { LoadReviews, LoadReviewsSuccess } from '../actions/reviews.actions';
import { LoadFavoriteReviews, LoadFavoriteReviewsSuccess, SetTotalFavoriteReviewPages } from '../actions/favorite_reviews.actions';
import { isPlatformServer } from '@angular/common';

@Injectable({
    providedIn: 'root'
})
export class ReviewsService {
    limit: number = 7; // the maximum amount of reviews per page - needed for our api call

    public questionSet: {vragenset_id: string}[];

    /*** Checks if reviews not loading and not loaded */
    public needSelectedReviews$: Observable<boolean> = this.store.pipe(
        select(fromRoot.needSelectedReviews)
    );

    public getSelectedProductName$: Observable<string> = this.store.pipe(
        select(fromRoot.getSelectedProductName)
    );

    public getSelectedPageNumber$: Observable<number> = this.store.pipe(
        select(fromRoot.getSelectedPageNumber)
    );

    /*** If not loading and not loaded, load reviews, and set success */
    public requireReviews$: Observable<Review[]> = this.needSelectedReviews$.pipe(
        filter(need => need),
        withLatestFrom(this.getSelectedPageNumber$, this.getSelectedProductName$),
        tap(([needSelectedReviews, page_number, product_name]) => this.store.dispatch(new LoadReviews( { product_id: product_name }))),
        switchMap(([needSelectedReviews, page_number, product_name]) => this.getReviews(product_name, page_number)),
        map((reviewData: {reviews: any[], totalReviewPages: number }) => {
            this.store.dispatch(new LoadReviewsSuccess(reviewData));
            return reviewData.reviews;
        }),
        share()
    );

    /***  Get reviews */
    public reviews$: Observable<Review[]> = muteFirst(
        this.requireReviews$.pipe(
            startWith([])
        ),
        this.store.pipe(
            select(fromRoot.getSelectedReviews)
        )
    );

    /*** Checks if fav_reviews not loading and not loaded */
    public needSelectedFavoriteReviews$: Observable<boolean> = this.store.pipe(
        select(fromRoot.needSelectedFavoriteReviews)
    );

    public getSelectedFavoriteProductName$: Observable<string> = this.store.pipe(
        select(fromRoot.getSelectedFavoriteProductName)
    );

    public getSelectedFavoritePageNumber$: Observable<number> = this.store.pipe(
        select(fromRoot.getSelectedFavoritePageNumber)
    );

    /*** If not loading and not loaded, load fav_reviews, and set success */
    public requireFavoriteReviews$: Observable<Review[]> =
        combineLatest([this.needSelectedFavoriteReviews$, this.getSelectedFavoritePageNumber$]).pipe(
        filter(([need, selected]) => need),
        withLatestFrom(this.getSelectedFavoriteProductName$),
        tap(([[needSelectedFavoriteReviews, page_number], product_name]) =>
            this.store.dispatch(new LoadFavoriteReviews( { product_id: product_name }))),
        switchMap(([[needSelectedFavoriteReviews, page_number], product_name]) => this.getReviews(product_name, page_number, 'J')),
        map((reviewData: {reviews: [], totalReviewPages: number }) => {
            this.store.dispatch(new LoadFavoriteReviewsSuccess({ reviews: reviewData.reviews }));
            this.store.dispatch(new SetTotalFavoriteReviewPages({ totalReviewPages: reviewData.totalReviewPages }));
            return reviewData.reviews;
        }),
        share()
    );

    /***  Get fav_reviews */
    public fav_reviews$: Observable<Review[]> = muteFirst(
        this.requireFavoriteReviews$.pipe(
            startWith([])
        ),
        this.store.pipe(
            select(fromRoot.getSelectedFavoriteReviews)
        )
    );

    constructor(
        private request: RequestService,
        private store: Store<fromRoot.State>,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {}

    public getReviews(product_id: string, pageNumber: number, alleen_favorieten_indicatie?: string):
        Observable<{reviews: any[], totalReviewPages: number}> {
        if (isPlatformServer(this.platformId)) {
            return of({
                reviews: [],
                totalReviewPages: 0
            });
        }

        if (product_id) {
            this.questionSet = this.questionSetData(product_id);
        }

        const vanaf_rijnummer = (pageNumber - 1) > 0 ? (pageNumber - 1) : 0;

        const payload = {
            vragenset: this.questionSet,
            filter: {
                rij_aantal: this.limit,
                vanaf_rijnummer: vanaf_rijnummer * this.limit,
                alleen_favorieten_indicatie: alleen_favorieten_indicatie
            },
        };

        return this.request.post('reviews-algemeen/opvragen/testimonial', payload).pipe(
            filter(b => b !== undefined),
            map(response => {
                return {
                    reviews: response.data.testimonials,
                    totalReviewPages: Math.ceil(response.data.totaal_aantal_rijen / this.limit)
                };
            }),
        );
    }

    private questionSetData(product_id: string): {vragenset_id: string}[] {
        // ook aanpassen in product-grade.service.ts
        const product_ids = {
            aansprakelijkheidsverzekering: [
                { vragenset_id: 1830 },
                { vragenset_id: 5751 }
            ],
            autoverzekering: [
                { vragenset_id: 1807 },
                { vragenset_id: 5748 }
            ],
            autoverzekering_landingpage: [
                { vragenset_id: 1807 },
                { vragenset_id: 5748 }
            ],
            brommerverzekering: [
                { vragenset_id: 1833 },
                { vragenset_id: 5750 }
            ],
            caravanverzekering: [
                { vragenset_id: 1835 },
                { vragenset_id: 5755 }
            ],
            dierenverzekering: [
                { vragenset_id: 1834 },
                { vragenset_id: 5757 }
            ],
            InShared: [
                { vragenset_id: 1374 },
                { vragenset_id: 5747 }
            ],
            motorverzekering: [
                { vragenset_id: 1831 },
                { vragenset_id: 5752 }
            ],
            ongevallenverzekering: [
                { vragenset_id: 1832 },
                { vragenset_id: 5754 }
            ],
            rechtsbijstandverzekering: [
                { vragenset_id: 1829 },
                { vragenset_id: 5756 }
            ],
            reisverzekering: [
                { vragenset_id: 1836 },
                { vragenset_id: 5753 }
            ],
            woonverzekering: [
                { vragenset_id: 1837 },
                { vragenset_id: 5749 }
            ],
            schade: [
                { vragenset_id: 4351 },
                { vragenset_id: 5758 }
            ],
            schadeherstel: [
                { vragenset_id: 4371 },
                { vragenset_id: 5759 }
            ],
            inboedelverzekering: [
                { vragenset_id: 5957 }
            ],
            opstalverzekering: [
                { vragenset_id: 5958 }
            ],
            fietsverzekering: [
                { vragenset_id: 7775 }
            ],
        };

        return product_ids[product_id];
    }
}
